import useSWR, { useSWRConfig } from 'swr';

import myShopClient from '@services/nearlive';
import shopApi from '@services/shop';

/**
 * Generic insights fetcher. The Lovable prototype distinguishes between
 * Local Listings (organic) and Local Ads (paid) — we expose them as
 * separate hooks so pages can subscribe to only what they show.
 */
async function fetchInsights(path, params) {
	const client = await myShopClient();
	const { data } = await client.get(path, { params });
	return data;
}

export function useLocalListings(shopId, { from, to } = {}) {
	const key = shopId ? ['unify:local-listings', shopId, from, to] : null;
	const { data, error, isLoading } = useSWR(key, () => fetchInsights(`/api/local-listings/${shopId}`, { from, to }), {
		revalidateOnFocus: false
	});
	return { data, error, isLoading };
}

export function useLocalAds(shopId, { from, to } = {}) {
	const key = shopId ? ['unify:local-ads', shopId, from, to] : null;
	const { data, error, isLoading, mutate } = useSWR(key, () => fetchInsights(`/api/local-ads/${shopId}`, { from, to }), {
		revalidateOnFocus: false
	});
	const { mutate: globalMutate } = useSWRConfig();

	/**
	 * Persist a new daily budget via the existing local-ads plan endpoint.
	 * On success, revalidate this SWR cache so the page reflects the new
	 * `budget.daily` / `summary.monthlySpend` next render.
	 */
	const updateBudget = async (budget) => {
		const client = await myShopClient();
		await client.put(`/api/channels/local-ads/plan/${shopId}`, { budget });
		// Local Ads is the score's one dynamic weight — revalidate the gauge too.
		await Promise.all([mutate(), globalMutate(['local-potential', shopId])]);
	};

	/**
	 * Activate Local Inventory Ads from the empty-state CTA. Sequences the two
	 * mutations that have to land for the page to flip into the populated
	 * branch: enable the `local-ads` channel (creates the row in DynamoDB if
	 * absent), then create the initial upcoming plan at the suggested daily
	 * budget. The same pair of endpoints the retailers-dashboard hits when a
	 * user starts their first Local Ads plan — no new backend surface.
	 *
	 * SWR mutate fires last so `data.enabled` flips to true and the page
	 * automatically transitions from the EmptyState to the live KPIs without a
	 * route change or hard reload.
	 */
	const activate = async (budget = 5) => {
		const client = await myShopClient();
		await client.post(`/api/channels/${shopId}/local-ads/enable`);
		await client.put(`/api/channels/local-ads/plan/${shopId}`, { budget });
		// Both this hook (so the page flips to populated) and useChannels (so
		// the sidebar's "+ Learn more" pill flips to "Live"). Without the global
		// mutate the sidebar reads stale channel data until the next page
		// refresh. The Local Potential gauge revalidates too (LIA is a score input).
		await Promise.all([mutate(), globalMutate(['unify:channels', shopId]), globalMutate(['local-potential', shopId])]);
	};

	/**
	 * Persist the retailer's average basket value on the shop row, then revalidate
	 * the shop (so `useShop` consumers see it) and this read (so the Total Basket
	 * Revenue projection recomputes server-side). Lives here rather than on the
	 * page so the Ads and Results pages can't drift apart.
	 */
	const updateAverageBasketValue = async (amount) => {
		if (!shopId) return;
		await shopApi.updateMyShop(shopId, { averageBasketValue: amount });
		await Promise.all([mutate(), globalMutate('unify:my-shops')]);
	};

	return { data, error, isLoading, mutate, updateBudget, activate, updateAverageBasketValue };
}

/**
 * Cross-shop benchmarks for Local Inventory Ads — currently surfaces the
 * "Industry average" CTR caption on the Ads page. Cached for a week
 * server-side, so a single revalidation per session is fine.
 */
export function useLocalAdsAverages() {
	const { data, error, isLoading } = useSWR('unify:local-ads:averages', () => fetchInsights('/api/local-ads/average-results'), {
		revalidateOnFocus: false
	});
	return { data, error, isLoading };
}

/**
 * Last-12-months totals for an insights table, via the generic
 * `POST /api/insights` endpoint (same path the Inventory accuracy page uses
 * for its upload-history strip). Used by the channel manage page to surface
 * real Google Local Listings performance — daily rows are fetched and summed
 * client-side. Returns `hasData: false` when the table has no rows for the
 * shop, so the caller can omit the Performance tiles rather than show zeros.
 */
export function useInsightsTotals(shopId, table, days = 365) {
	const key = shopId && table ? ['unify:insights-totals', shopId, table, days] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const to = new Date();
			const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
			const iso = (d) => d.toISOString().slice(0, 10);
			const { data } = await client.post('/api/insights', {
				id: [shopId],
				table,
				columns: ['date', 'impressions', 'clicks'],
				from: iso(from),
				to: iso(to),
				sort: [{ column: 'date', order: 'asc' }]
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
	const sum = (column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);
	return { impressions: sum('impressions'), clicks: sum('clicks'), hasData: rows.length > 0, error, isLoading };
}

/**
 * Last-N-day Google Business Profile totals (`gmb` insights table), summed
 * client-side. Returns the same shape as `useInsightsTotals` but with GBP-
 * native column names rolled up into two figures:
 *   - `views` — search + map views (desktop + mobile) on the Business Profile
 *   - `interactions` — website + phone + directions clicks
 * Pair this with `useInsightsTotals(.., 'local_listings')` on the Google
 * channel page to surface the full organic Google footprint, not just the
 * Local Inventory subset (NEA-5879).
 */
export function useGbpTotals(shopId, days = 365) {
	const key = shopId ? ['unify:gbp-totals', shopId, days] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const to = new Date();
			const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
			const iso = (d) => d.toISOString().slice(0, 10);
			const { data } = await client.post('/api/insights', {
				id: [shopId],
				table: 'gmb',
				columns: [
					'date',
					'map_views_desktop',
					'map_views_mobile',
					'search_views_desktop',
					'search_views_mobile',
					'website_clicks',
					'phone_clicks',
					'directions_clicks'
				],
				from: iso(from),
				to: iso(to),
				sort: [{ column: 'date', order: 'asc' }]
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
	const sum = (column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);
	const views = sum('search_views_desktop') + sum('search_views_mobile') + sum('map_views_desktop') + sum('map_views_mobile');
	const interactions = sum('website_clicks') + sum('phone_clicks') + sum('directions_clicks');
	return { views, interactions, hasData: rows.length > 0, error, isLoading };
}

const sumColumn = (rows, column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);

const pctChange = (current, previous) => {
	if (!previous) return current ? 100 : 0;
	return Number((((current - previous) / previous) * 100).toFixed(1));
};

// Google's GMB pipeline lands `map_views_*` first and `search_views_*` a few
// days later, so a shop's trailing rows arrive with maps populated and search
// still at 0. Plotted as-is, that's a chart-edge nosedive. The lag isn't fixed
// (median ~5 days across the estate, but with a long tail — some shops sit at
// 7+ for weeks), so a fixed shift under-corrects for shops on that tail.
// Instead, trim from the end of the returned series while both conditions
// hold: `search_views_* === 0` AND `map_views_* > 0`. Stop as soon as we hit
// a row with search > 0 — earlier legitimate zero-search days are preserved.
// Guarded: if no row in the window registers any search views, leave the
// series intact rather than trimming it to nothing (a shop with genuinely
// no search history — or a series that returned empty).
function trimTrailingUnsettledGbpRows(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return rows;
	const searchViews = (r) => Number(r.search_views_desktop || 0) + Number(r.search_views_mobile || 0);
	const mapsViews = (r) => Number(r.map_views_desktop || 0) + Number(r.map_views_mobile || 0);
	const hasAnySettledSearch = rows.some((r) => searchViews(r) > 0);
	if (!hasAnySettledSearch) return rows;
	let end = rows.length;
	while (end > 0 && searchViews(rows[end - 1]) === 0 && mapsViews(rows[end - 1]) > 0) end -= 1;
	return end === rows.length ? rows : rows.slice(0, end);
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Analytics rows arrive as 'YYYY-MM-DD' from the Data API, but a driver that
// handed back a Date would silently miss every join below.
const isoDay = (value) =>
	typeof value === 'string' ? value.slice(0, 10) : value instanceof Date ? value.toISOString().slice(0, 10) : null;

// Shift a plain date string by whole days. Parsed as UTC so a DST boundary
// can't move the result onto the wrong day.
const shiftDay = (iso, deltaDays) => {
	if (!iso) return null;
	const parsed = new Date(`${iso}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return null;
	return new Date(parsed.getTime() + deltaDays * DAY_MS).toISOString().slice(0, 10);
};

/**
 * How far back a day's counterpart in the previous window sits.
 *
 * Read off the resolved window rather than re-derived from `range.days`. The
 * two are equal today — `useDateRange` builds the windows adjacent, so the gap
 * is the window length plus one — but re-deriving the rule here would encode it
 * in a second place with nothing linking them, and no test wires a real
 * `buildRange` result through this join. If the lookback construction ever
 * changes (the picker's comparison dropdown reserves "Same period last year"),
 * the difference between the two window starts stays correct where `days + 1`
 * would silently pair nothing, dropping every delta pill with no failure.
 */
const lookbackOffsetDays = (range) => {
	const from = Date.parse(`${isoDay(range?.from)}T00:00:00Z`);
	const lookbackFrom = Date.parse(`${isoDay(range?.lookbackFrom)}T00:00:00Z`);
	if (Number.isNaN(from) || Number.isNaN(lookbackFrom)) return null;
	// Both parsed as UTC, so a DST boundary can't shift the result by a day.
	const offset = Math.round((from - lookbackFrom) / DAY_MS);
	return offset > 0 ? offset : null;
};

/**
 * Two POST /api/insights calls in parallel — current window plus the
 * matching previous window for the delta pills. Returns the merged result.
 * Both windows share the SWR cache key so changing the date picker
 * invalidates the pair atomically.
 */
async function fetchRangeWithLookback(query, { from, to, lookbackFrom, lookbackTo }) {
	const client = await myShopClient();
	const [{ data: current }, { data: previous }] = await Promise.all([
		client.post('/api/insights', { ...query, from, to }),
		client.post('/api/insights', { ...query, from: lookbackFrom, to: lookbackTo })
	]);
	return { current: Array.isArray(current) ? current : [], previous: Array.isArray(previous) ? previous : [] };
}

/**
 * Google Business Profile daily insights for the active range plus the
 * matching previous period, used by the Results page's Google Local Listings
 * section (KPI tiles, profile-views line, search-vs-maps split, interactions
 * breakdown / time-series). Sources columns mirror the retailers-dashboard
 * BusinessProfile insights page so the numbers reconcile across products.
 */
export function useGbpInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:gbp', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		() =>
			fetchRangeWithLookback(
				{
					id: [shopId],
					table: 'gmb',
					columns: [
						'date',
						'map_views_desktop',
						'map_views_mobile',
						'search_views_desktop',
						'search_views_mobile',
						'website_clicks',
						'phone_clicks',
						'directions_clicks'
					],
					sort: [{ column: 'date', order: 'asc' }]
				},
				range
			),
		{ revalidateOnFocus: false }
	);
	const current = trimTrailingUnsettledGbpRows(data?.current ?? []);
	const previousTrimmed = trimTrailingUnsettledGbpRows(data?.previous ?? []);
	const offsetDays = lookbackOffsetDays(range);
	// Bound the previous window to the stretch that actually has a counterpart
	// in the (post-trim) current window, so the delta pills compare the same
	// span of days. The trim typically costs current ~5 recent days to pipeline
	// lag and costs previous nothing, which is the asymmetry this corrects.
	//
	// This must be done by DATE, not by row count. Chopping `current` down to
	// `previous.length` — which is what this used to do — silently discards the
	// range the retailer asked for whenever their history is shorter than the
	// window: a shop with 77 days of lookback had a 12-month request rendered as
	// its first 77 days (NEA-6380). `current` is what was asked for and is never
	// truncated; `previous` is simply however much of the matching span exists.
	const currentEnd = isoDay(current[current.length - 1]?.date);
	const previousCutoff = offsetDays && currentEnd ? shiftDay(currentEnd, -offsetDays) : null;
	const previous = previousCutoff ? previousTrimmed.filter((row) => isoDay(row.date) <= previousCutoff) : previousTrimmed;
	const totalViews = (row) =>
		Number(row.search_views_desktop || 0) +
		Number(row.search_views_mobile || 0) +
		Number(row.map_views_desktop || 0) +
		Number(row.map_views_mobile || 0);
	const searchViews = (row) => Number(row.search_views_desktop || 0) + Number(row.search_views_mobile || 0);
	const mapsViews = (row) => Number(row.map_views_desktop || 0) + Number(row.map_views_mobile || 0);
	const interactions = (row) => Number(row.website_clicks || 0) + Number(row.phone_clicks || 0) + Number(row.directions_clicks || 0);
	const totals = (rows) => ({
		profileViews: rows.reduce((t, r) => t + totalViews(r), 0),
		searchViews: rows.reduce((t, r) => t + searchViews(r), 0),
		mapsViews: rows.reduce((t, r) => t + mapsViews(r), 0),
		interactions: rows.reduce((t, r) => t + interactions(r), 0),
		website: sumColumn(rows, 'website_clicks'),
		directions: sumColumn(rows, 'directions_clicks'),
		phone: sumColumn(rows, 'phone_clicks')
	});
	const currentTotals = totals(current);
	const previousTotals = totals(previous);
	const previousByDate = new Map(previous.map((row) => [isoDay(row.date), row]));

	// Join each current day to its counterpart once — the chart series and the
	// delta pills both need it, and doing it twice invites them to disagree.
	const joined = current.map((row) => {
		const date = isoDay(row.date);
		const prevDate = offsetDays ? shiftDay(date, -offsetDays) : null;
		return { row, date, prevDate, prevRow: (prevDate && previousByDate.get(prevDate)) || null };
	});

	// The pills must compare the SAME set of days on both sides. The two windows
	// legitimately cover different numbers of days — a shop whose GBP history
	// starts partway through the lookback window has fewer previous rows — and
	// summing unequal spans reports a flat shop as a huge rise: 9 current days
	// against 2 previous days reads as +485% (NEA-6380). So the deltas are
	// computed over the paired days only, while `totals` and `series` keep the
	// whole window the retailer asked for.
	const paired = joined.filter((entry) => entry.prevRow);
	const pairedCurrentTotals = totals(paired.map((entry) => entry.row));
	const pairedPreviousTotals = totals(paired.map((entry) => entry.prevRow));
	// Nothing overlaps at all — a genuinely new shop. `null` makes the tile drop
	// the pill rather than show a fabricated +100% against a baseline of zero.
	const change = (key) => (paired.length ? pctChange(pairedCurrentTotals[key], pairedPreviousTotals[key]) : null);

	return {
		isLoading,
		error,
		hasData: current.length > 0,
		totals: currentTotals,
		previousTotals,
		// How many days the deltas above are actually drawn from. Less than
		// `series.length` means the previous window only partly covers this one.
		comparedDays: paired.length,
		change: {
			profileViews: change('profileViews'),
			searchViews: change('searchViews'),
			mapsViews: change('mapsViews'),
			interactions: change('interactions')
		},
		// Each point carries its own counterpart from the previous window, joined
		// on the real date rather than array position — two windows of differing
		// row density otherwise pair unrelated days, which is what made the
		// tooltip offer "22 Sep 2025 vs 7 Jul 2025" (NEA-6380). `prevDate` is
		// only set when a matching row exists, so the tooltip never names a day
		// it has no figure for.
		series: joined.map(({ row, date, prevDate, prevRow }) => ({
			date,
			total: totalViews(row),
			search: searchViews(row),
			maps: mapsViews(row),
			website: Number(row.website_clicks || 0),
			directions: Number(row.directions_clicks || 0),
			phone: Number(row.phone_clicks || 0),
			prev: prevRow ? totalViews(prevRow) : null,
			prevDate: prevRow ? prevDate : null
		}))
	};
}

/**
 * Daily impressions + clicks for the Google Local Listings (organic) table.
 * Returns the per-day series and pre-summed totals + previous-period totals
 * so the Lovable design's KPI tiles render without extra reduce work.
 */
export function useLocalListingsInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:local-listings:daily', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		() =>
			fetchRangeWithLookback(
				{
					id: [shopId],
					table: 'local_listings',
					columns: ['date', 'impressions', 'clicks'],
					sort: [{ column: 'date', order: 'asc' }]
				},
				range
			),
		{ revalidateOnFocus: false }
	);
	const current = data?.current ?? [];
	const previous = data?.previous ?? [];
	const impressions = sumColumn(current, 'impressions');
	const clicks = sumColumn(current, 'clicks');
	const previousImpressions = sumColumn(previous, 'impressions');
	const previousClicks = sumColumn(previous, 'clicks');
	return {
		isLoading,
		error,
		hasData: current.length > 0,
		impressions,
		clicks,
		previousImpressions,
		previousClicks,
		change: {
			impressions: pctChange(impressions, previousImpressions),
			clicks: pctChange(clicks, previousClicks)
		},
		series: current
	};
}

async function fetchStoredQuery(category, queryId, params) {
	const client = await myShopClient();
	const path = category
		? `/api/insights/stored-query/${encodeURIComponent(category)}/${encodeURIComponent(queryId)}`
		: `/api/insights/stored-query/${encodeURIComponent(queryId)}`;
	const { data } = await client.get(path, { params });
	return data;
}

/**
 * Stored-query wrapper for the GET /api/insights/stored-query endpoint. SQL
 * lives in `interfaces/library/api-handlers/src/handlers/insights/stored-queries`
 * — the category/queryId combo selects which one runs. Passes the active
 * shop, range and any extra params straight through.
 */
export function useStoredQuery(shopId, category, queryId, { from, to, ...rest } = {}) {
	const key = shopId && queryId && from && to ? ['unify:stored-query', category, queryId, shopId, from, to, JSON.stringify(rest)] : null;
	const { data, error, isLoading } = useSWR(key, () => fetchStoredQuery(category, queryId, { shopIds: shopId, from, to, ...rest }), {
		revalidateOnFocus: false
	});
	return { data: Array.isArray(data) ? data : [], error, isLoading };
}

/**
 * Top-performing organic Local Listings products for the active range.
 * Backed by the local-listings/popular-products stored query — the same SQL
 * the retailers-dashboard Local Listings page uses for its Popular products
 * table, joining product_local_listings_weekly with analytics_events for
 * website-redirect counts.
 */
export function usePopularLocalListings(shopId, range) {
	return useStoredQuery(shopId, 'local-listings', 'popular-products', {
		from: range?.from,
		to: range?.to,
		includeRedirects: 'false'
	});
}

/**
 * Storefront analytics from the `analytics_events` table — totals,
 * distinct visitors, and a referrer breakdown for the active range. Scoped
 * to `domain = 'shopping'` (the value the shopping-service tracking layer
 * emits — see `channels/services/shopping-service/lib/tracking/index.js`).
 *
 * KPI labels reflect what the shopping-service genuinely emits:
 *   - distinct visitor_id values (unique visitors)
 *   - `product-page-view` events (product page views — fires on every
 *     storefront product detail render)
 *   - `product-page-reservation-started` / `product-page-order-started` /
 *     `basket-order-started` / `basket-reservation-started`
 *     (reservation/checkout funnel starts, incl. the NEA-6028 basket flow)
 *   - the five `shop-page-*-clicked` events (shop info clicks)
 *
 * We deliberately do NOT surface `product-website-redirect` here — that
 * endpoint is primarily the destination for Google Local Inventory Ads
 * click-throughs, not a storefront-specific interaction.
 *
 * Aggregation runs server-side in `storefront/overview-totals` (NEA-5858) —
 * single-row counts, with lookback.
 *
 * The sibling `storefront/overview-traffic` query is deliberately NOT fetched
 * (NEA-6380). Its Traffic sources card was dropped in `a337444b19`, but the
 * fetch and its client-side referrer rollup were left behind — so every
 * Results and Overview load was paying for a result nothing rendered. It
 * returns one row per distinct referrer URL and `stored-query.js` pages it to
 * exhaustion, which this ticket's 90-day → 12-month ceiling would have made
 * roughly four times worse. The SQL and the stored-query route are untouched,
 * so bringing the card back is re-adding the fetch.
 */
export function useStorefrontInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:storefront:stored', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const totals = await fetchStoredQuery('storefront', 'overview-totals', {
				shopIds: shopId,
				from: range.from,
				to: range.to,
				// Lookback drives `change.*` pills; safe to omit when the
				// caller doesn't compute one (e.g. tests).
				...(range.lookbackFrom && range.lookbackTo ? { lookbackFrom: range.lookbackFrom, lookbackTo: range.lookbackTo } : {})
			});
			return { totals };
		},
		{ revalidateOnFocus: false }
	);

	// `overview-totals` returns `{ current: [row], lookback: [row] }` when the
	// lookback range was passed, otherwise a flat array of one row.
	const totalsCurrent = Array.isArray(data?.totals) ? data.totals[0] : data?.totals?.current?.[0];
	const totalsLookback = Array.isArray(data?.totals) ? undefined : data?.totals?.lookback?.[0];

	const visitors = Number(totalsCurrent?.visitors || 0);
	const previousVisitors = Number(totalsLookback?.visitors || 0);
	const productViews = Number(totalsCurrent?.product_views || 0);
	const previousProductViews = Number(totalsLookback?.product_views || 0);
	const reservationStarts = Number(totalsCurrent?.reservation_starts || 0);
	const previousReservationStarts = Number(totalsLookback?.reservation_starts || 0);
	const shopPageClicks = Number(totalsCurrent?.shop_page_clicks || 0);
	const previousShopPageClicks = Number(totalsLookback?.shop_page_clicks || 0);

	return {
		isLoading,
		error,
		hasData: Number(totalsCurrent?.total_events || 0) > 0,
		visitors,
		productViews,
		reservationStarts,
		shopPageClicks,
		change: {
			visitors: pctChange(visitors, previousVisitors),
			productViews: pctChange(productViews, previousProductViews),
			reservationStarts: pctChange(reservationStarts, previousReservationStarts),
			shopPageClicks: pctChange(shopPageClicks, previousShopPageClicks)
		}
	};
}

/**
 * Top viewed products on the storefront — counts `product-page-view`
 * events grouped by the barcode in the event attributes. Backed by the
 * `storefront/popular-products` stored query.
 */
export function useStorefrontPopularProducts(shopId, range) {
	// Bounded because `stored-query.js` otherwise pages to exhaustion at 500 rows
	// a page — one row per distinct barcode viewed in the window, re-running the
	// whole aggregate per page — while the Results page renders the top 6. A
	// caller-supplied `limit` wins (`restOfParams` spreads after the default) and
	// ends the loop on the first page. 50 leaves headroom for a longer list
	// without the 12-month ceiling turning one card into several round trips.
	return useStoredQuery(shopId, 'storefront', 'popular-products', { from: range?.from, to: range?.to, limit: 50 });
}

/**
 * Batch-resolve barcode → title via the per-shop stock endpoint. The
 * shopping-service tracker only ships `barcode` in `product-page-view`
 * attributes — `title` isn't recorded on the event — so for production
 * data the popular-products stored query returns titles as null. This
 * hook fills them in by looking each barcode up against the shop's stock
 * (where the title is merged in by stock-gli-service from products + custom
 * products). N small GETs in parallel for the 6-or-so visible barcodes is
 * cheap; a single SWR key keeps the whole batch cached together.
 */
export function useStockTitles(shopId, barcodes) {
	const list = Array.isArray(barcodes) ? barcodes.filter(Boolean) : [];
	const key = shopId && list.length ? ['unify:stock-titles', shopId, list.join('|')] : null;
	const { data, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			try {
				const { data: items } = await client.post(`/api/inventory/${shopId}/batch`, { barcodes: list });
				return Object.fromEntries((items || []).map((item) => [item.barcode, item.title || null]));
			} catch {
				return {};
			}
		},
		{ revalidateOnFocus: false }
	);
	return { titles: data || {}, isLoading };
}

/*
 * `classifyReferrer` lived here — it mapped a raw referrer URL onto a friendly
 * traffic-source label ("Within Near.st", "Google Search", …) for the Storefront
 * Traffic sources card. That card was dropped in `a337444b19`; the rollup and
 * its `overview-traffic` fetch outlived it until NEA-6380. Recover both from
 * that commit if the card comes back — the SQL was never removed.
 */
